import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type {
  CalculatorFormatValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { textColorMixin, textStyleMixin } from '@elfsight/widget-ui';
import { createReadableClass, roundTo4 } from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import { useRect } from 'react-use-rect';
import type { Field } from '../../contexts';
import { commonFieldsSettings } from '../commonFieldSettings';
import { LINE_HEIGHT_RATIO } from '../constants';
import { NumberCalculatorComponent as Number } from '../number-calculator';
import type { SliderAPI } from './components/slider';
import { SliderComponent } from './components/slider';

export type NumberSliderProps = Omit<Field, 'value'> & {
  value: number;
  initialValue: number;
  label?: string;
  required?: boolean;
  showLabel?: boolean;
  decimalSeparator?: string;

  format: CalculatorFormatValue;

  fieldActiveColor?: string;
  fieldBorderType?: 'outline' | 'underline' | null;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldBorderRadius?: number;
  fieldFillColor?: string;

  fieldLabelPosition?: string;
  fieldLabelColor?: string;
  fieldLabelFontStyle?: TextStyleValue;

  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;

  onChange: (
    value: number | undefined,
    event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;

  minValue: number;
  maxValue: number;
  step?: number;

  numberFormatter: (value: number) => string;
};

export function Slider({
  id,
  value,
  initialValue,
  label,
  decimalSeparator,
  showLabel = true,
  required = false,
  fieldActiveColor = commonFieldsSettings.fieldActiveColor,
  fieldLabelColor = commonFieldsSettings.fieldLabelColor,
  fieldLabelPosition = commonFieldsSettings.fieldLabelPosition,
  fieldLabelFontStyle = commonFieldsSettings.fieldLabelFontStyle,
  fieldColor = commonFieldsSettings.fieldColor,
  fieldFillColor = commonFieldsSettings.fieldFillColor,
  fieldFontStyle = commonFieldsSettings.fieldFontStyle,
  fieldBorderType = commonFieldsSettings.fieldBorderType,
  fieldBorderColor = commonFieldsSettings.fieldBorderColor,
  fieldBorderWidth = commonFieldsSettings.fieldBorderWidth,
  fieldBorderRadius = commonFieldsSettings.fieldBorderRadius,
  numberFormatter,
  format,
  onChange,
  maxValue = 0,
  minValue = 100,
  step
}: NumberSliderProps) {
  const fullLabel = label && `${label} ${required ? '*' : ''}`;
  const sliderAPIRef = useRef<SliderAPI>(null);
  const [isFocused, setIsFocused] = useState(false);

  const sliderOnChange = (nextVal: number) => {
    onChange(nextVal);
  };

  useEffect(() => {
    if (isFocused) return;
    const clampedValue = clamp(value, minValue, maxValue);
    onChange(clampedValue);
    sliderAPIRef.current?.setValue(clampedValue);
  }, [minValue, maxValue, value, isFocused]);

  useEffect(() => {
    const clampedValue = clamp(initialValue, minValue, maxValue);
    onChange(clampedValue);
    sliderAPIRef.current?.setValue(clampedValue);
  }, [minValue, maxValue, initialValue]);

  const [width, setWidth] = useState(0);
  const [ref] = useRect(({ width }) => setWidth(width), { resize: true });
  const labelDirection = width < 300 ? 'column' : 'row';
  const displayLabel = showLabel && !!label && fieldLabelPosition === 'inside';

  return (
    <Container ref={ref}>
      <InputRow _direction={labelDirection}>
        {displayLabel && (
          <Label
            htmlFor={id}
            _textStyle={fieldLabelFontStyle}
            _textColor={fieldLabelColor}
          >
            {fullLabel}
          </Label>
        )}
        <Number
          id={id}
          className={createReadableClass({
            component: 'fields',
            element: 'slider-number'
          })}
          value={value}
          format={format}
          showLabel={false}
          label={fullLabel}
          decimalSeparator={decimalSeparator}
          numberFormatter={numberFormatter}
          initialValue={initialValue.toString()}
          fieldColor={fieldColor}
          fieldFillColor={fieldFillColor}
          fieldFontStyle={fieldFontStyle}
          fieldBorderType={fieldBorderType ? fieldBorderType : undefined}
          fieldBorderColor={fieldBorderColor}
          fieldBorderWidth={fieldBorderWidth}
          fieldBorderRadius={fieldBorderRadius}
          fieldActiveColor={fieldActiveColor}
          minValue={minValue}
          maxValue={maxValue}
          fitContentWidth={labelDirection === 'row'}
          onChange={(value, event) => {
            onChange(value, event);
            sliderAPIRef.current?.setValue(value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={(newVal) => {
            setIsFocused(false);
            onChange(newVal);
            sliderAPIRef.current?.setValue(newVal);
          }}
        />
      </InputRow>

      <SliderComponent
        ref={sliderAPIRef}
        initialValue={initialValue}
        maxValue={maxValue}
        minValue={minValue}
        step={step}
        fieldActiveColor={fieldActiveColor}
        fieldColor={fieldColor}
        numberFormatter={numberFormatter}
        ariaLabel={fullLabel}
        onChange={sliderOnChange}
      />
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'slider-container'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InputRow = styled.div.attrs<{ _direction?: string }>({
  className: createReadableClass({
    component: 'fields',
    element: 'slider-input-row'
  })
})`
  display: flex;

  ${({ _direction = 'row' }) =>
    _direction === 'row'
      ? css`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        `
      : css`
          flex-direction: column;
          gap: 12px;
        `}
  & > div {
    flex-shrink: 0;
  }
`;

const Label = styled.label.attrs<{
  _textStyle: TextStyleValue;
  _textColor: string;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'slider-label'
  })
})`
  display: block;
  line-height: ${({ _textStyle }) =>
    roundTo4(_textStyle.fontSize * LINE_HEIGHT_RATIO)}px;
  ${textColorMixin}
  ${textStyleMixin}
`;
